import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@repo/ui";
import { useAppSelector, useAppDispatch } from "../../../hooks/useAppDispatch";
import { removeFromCart } from "../../../redux/slices/guestCartSlice";
import { getProduct, Product } from "../../../services/productApiService";
import { withTranslations } from "../../../hoc/withTranslations/withTranslations";
import { useMediaQuery } from "react-responsive";
import { useNavigate, useParams } from "react-router-dom";
import { RootState } from "../../../redux/store";

import trashIcon from "../../../assets/icons/trash.svg";
import placeholderImage from "../../../assets/placeholders/picture.webp";

const LOCALE = "modules_Guests_components_CartContent";

interface FullCartProduct {
  local: {
    productID: string;
    quantity: number;
    value: number;
  };
  api: Product;
}

const CartContent: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const { id } = useParams<{ id: string }>(); // Get giftTableID from URL params
  const giftTableIDFromState = useAppSelector(
    (state: RootState) => state.guestGiftTable.ID
  );
  const giftTableID = id || giftTableIDFromState; // Use ID from params or fallback to state

  const cartItems = useAppSelector((state) => state.guestCart.items);
  const dispatch = useAppDispatch();
  const [fullCartProducts, setFullCartProducts] = useState<FullCartProduct[]>(
    []
  );
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      const productDetails = await Promise.all(
        cartItems.map(async (item) => {
          const apiProduct = await getProduct(item.productID);
          return { local: item, api: apiProduct };
        })
      );
      setFullCartProducts(productDetails);
    };

    fetchProducts();
  }, [cartItems]);

  const handleRemoveItem = (productID: string) =>
    dispatch(removeFromCart(productID));

  const currencyFormat = (value: number) =>
    value.toLocaleString("es-MX", {
      style: "currency",
      currency: t("currencyCode"),
    });

  const totalPrice = fullCartProducts.reduce((total, item) => {
    const displayPrice =
      item.api.display_total_charge ?? item.api.display_price;
    return total + displayPrice * item.local.quantity;
  }, 0);

  const handleCheckoutClick = () => {
    if (cartItems.length > 0) {
      navigate(`/guests/checkout/dedication/${giftTableID}`);
    }
  };

  return (
    <div className="flex flex-col md:flex-row justify-between p-4 space-y-4 md:space-y-0 md:space-x-8">
      {/* Left Column: Cart Items */}
      <div className="flex-1 space-y-4">
        {fullCartProducts.length === 0 ? (
          <p className="text-center text-gray-500">{t("cart.empty")}</p>
        ) : (
          fullCartProducts.map((item) => (
            <div
              key={item.local.productID}
              className="flex justify-between items-center p-4 border rounded-lg shadow-md bg-white"
            >
              <div className="flex items-center space-x-4">
                <img
                  src={item.api.pictures?.[0]?.trim() || placeholderImage}
                  alt={item.api.title}
                  className="w-24 h-24 object-cover"
                />
                <div>
                  <h3 className="font-bold">{item.api.title}</h3>
                  <p className="text-sm">
                    {t("cart.quantity", { count: item.local.quantity })}
                  </p>
                  <p className="font-semibold text-lg">
                    {currencyFormat(
                      item.api.display_total_charge ?? item.api.display_price
                    )}
                  </p>
                </div>
              </div>
              <Button
                label={
                  <img
                    src={trashIcon}
                    alt={t("cart.remove")}
                    className="h-6 w-6"
                  />
                }
                onClick={() => handleRemoveItem(item.local.productID)}
                className="text-red-500 text-sm mt-2"
                type="secondary"
              />
            </div>
          ))
        )}
      </div>

      {/* Right Column: Total and Checkout for Desktop */}
      {!isMobile && fullCartProducts.length > 0 && (
        <div className="w-full md:w-1/3 flex flex-col items-center space-y-4">
          <div className="w-full bg-gray-100 p-4 rounded-lg shadow-md space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-lg font-bold">{t("cart.totalLabel")}</span>
              <span className="text-xl font-bold">
                {currencyFormat(totalPrice)}
              </span>
            </div>
            <Button
              className="w-full"
              label={t("cart.checkoutButton")}
              onClick={handleCheckoutClick}
              disabled={cartItems.length === 0}
            />
            <p className="text-xs text-gray-500 text-center">
              {t("cart.disclaimer")}
            </p>
          </div>
        </div>
      )}

      {/* Sticky Bottom Bar for Mobile */}
      {isMobile && fullCartProducts.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 rounded-t-3xl bg-white p-4 shadow-[0_-12px_30px_rgba(15,23,42,0.12)]">
          <div className="flex justify-between items-center mb-4">
            <span className="text-lg font-bold">{t("cart.totalLabel")}</span>
            <span className="text-xl font-bold">
              {currencyFormat(totalPrice)}
            </span>
          </div>
          <Button
            className="w-full"
            label={t("cart.checkoutButton")}
            onClick={handleCheckoutClick}
            disabled={cartItems.length === 0}
          />
          <p className="text-xs text-gray-500 text-center mt-2">
            {t("cart.disclaimer")}
          </p>
        </div>
      )}
    </div>
  );
};

export default withTranslations(LOCALE)(CartContent);
